import { db, auth } from '../firebase';
import { collection, doc, getDocs, serverTimestamp, writeBatch, query, where, getDoc } from 'firebase/firestore';
import { MasterTransaction } from '../types';

export interface JarTip {
  id?: string;
  tenantId: string;
  staffId?: string;
  submitterName: string;
  email: string;
  jarAmount: number;
  tipDate: string;
  shift?: string;
  status: 'Pending' | 'Calculated' | 'Approved' | 'Allocated' | 'Rejected';
  paymentStatus: 'Paid' | 'Unpaid';
  payoutMethod?: string;
  source: 'Jar';
  approverId?: string;
  approvalTimestamp?: any;
  masterTxId?: string;
  posId?: string;
  locationId?: string;
  locationName?: string;
}

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
    },
    operationType,
    path
  }
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

export const getJarTips = async (tenantId: string): Promise<JarTip[]> => {
  const tipsRef = collection(db, 'tenants', tenantId, 'jar_tips');
  try {
    const querySnapshot = await getDocs(tipsRef);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as JarTip));
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, 'tenants/' + tenantId + '/jar_tips');
    return [];
  }
};

export const getPendingJarTips = async (tenantId: string): Promise<JarTip[]> => {
  const tipsRef = collection(db, 'tenants', tenantId, 'jar_tips');
  const q = query(tipsRef, where('status', '==', 'Pending'));
  try {
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as JarTip));
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, 'tenants/' + tenantId + '/jar_tips');
    return [];
  }
};

export const getJarTipsByEmail = async (tenantId: string, email: string): Promise<JarTip[]> => {
  const tipsRef = collection(db, 'tenants', tenantId, 'jar_tips');
  const q = query(tipsRef, where('email', '==', email));
  try {
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as JarTip));
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, 'tenants/' + tenantId + '/jar_tips');
    return [];
  }
};

import { getSettings } from './settingsService';

const removeUndefined = (obj: any) => {
  const clean: any = {};
  Object.keys(obj).forEach(key => {
    if (obj[key] !== undefined) {
      clean[key] = obj[key];
    }
  });
  return clean;
};

export const submitJarTip = async (tenantId: string, tip: JarTip) => {
  const settings = await getSettings(tenantId);
  const payoutMethod = settings?.payoutMethod || 'Paycheck';
  const effectiveLocationId = tip.locationId && tip.locationId !== 'all' ? tip.locationId : 'loc-downtown';
  const effectiveLocationName = tip.locationName || (effectiveLocationId === 'loc-downtown' ? 'Downtown Bistro' : 'Store Location');
  const tipWithMethod = { ...tip, payoutMethod, locationId: effectiveLocationId, locationName: effectiveLocationName };
  
  const batch = writeBatch(db);
  const tipRef = doc(collection(db, 'tenants', tenantId, 'jar_tips'));
  const masterRef = doc(collection(db, 'tenants', tenantId, 'master_transactions'));
  
  batch.set(tipRef, removeUndefined({ ...tipWithMethod, masterTxId: masterRef.id }));

  const tx: MasterTransaction = {
    id: masterRef.id,
    tenantId,
    timestamp: serverTimestamp(),
    source: 'Jar',
    type: 'Submitted',
    reportingStatus: 'jar_submitted',
    amount: tip.jarAmount,
    submittedAmount: tip.jarAmount,
    payoutMethod,
    tipDate: tip.tipDate,
    shiftName: tip.shift || null,
    staffId: tip.staffId || 'unknown',
    staffName: tip.submitterName,
    email: tip.email,
    submittedBy: tip.submitterName,
    submissionStatus: 'Pending',
    allocationStatus: 'Pending',
    payrollIncluded: false,
    analyticsIncluded: true,
    auditVerified: false,
    auditContext: 'Initial Submission',
    relatedTransactionId: tipRef.id,
    source_id: tipRef.id,
    posId: tip.posId || null,
    locationId: effectiveLocationId,
    locationName: effectiveLocationName
  };
  batch.set(masterRef, removeUndefined(tx));

  try {
    await batch.commit();
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, 'tenants/' + tenantId + '/jar_tips/master_transactions');
  }
};

export const updateJarTipStatus = async (
  tenantId: string, 
  tipId: string, 
  masterTxId: string,
  updates: {
    status: JarTip['status'],
    paymentStatus?: 'Paid' | 'Unpaid',
    approverId?: string,
    allocatedAmount?: number
  }
) => {
  try {
    const tipRef = doc(db, 'tenants', tenantId, 'jar_tips', tipId);
    
    // Fallback: Verify master transaction existence or find it
    let finalMasterTxId = masterTxId;
    let masterRef = doc(db, 'tenants', tenantId, 'master_transactions', finalMasterTxId);
    let masterDoc = await getDoc(masterRef);

    if (!masterDoc.exists() || finalMasterTxId === 'unknown') {
      const q = query(
        collection(db, 'tenants', tenantId, 'master_transactions'), 
        where('relatedTransactionId', '==', tipId)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        finalMasterTxId = snapshot.docs[0].id;
        masterRef = doc(db, 'tenants', tenantId, 'master_transactions', finalMasterTxId);
      } else {
        console.warn(`MasterTransaction not found for tip ${tipId}, skipping master update for this tip.`);
        // Proceed with only updating the tip record if master is missing
        finalMasterTxId = ''; 
      }
    }

    const batch = writeBatch(db);
    batch.update(tipRef, { 
      ...updates,
      approvalTimestamp: serverTimestamp()
    });
    if (finalMasterTxId) {
      batch.update(masterRef, {
        allocatedAmount: updates.allocatedAmount,
        submissionStatus: updates.status === 'Approved' ? 'Finalized' : (updates.status === 'Rejected' ? 'Rejected' : 'Pending'),
        reportingStatus: updates.status === 'Approved' ? 'jar_allocated' : 'jar_submitted',
        auditVerified: updates.status === 'Approved',
        auditActor: updates.approverId || 'system',
        auditTimestamp: serverTimestamp()
      });
    }

    await batch.commit();
  } catch (error) {
    handleFirestoreError(error, OperationType.UPDATE, `tenants/${tenantId}/tip_approval_flow/${tipId}`);
  }
};
